import { supabase } from '../lib/supabase';

export interface PropostaMeta {
  status?: string;
  consideracoes?: string;
  condicoes_comerciais?: string;
  condicoes_pagamento?: string;
  total_geral?: number;
}

const META_PREFIX = '__META__:';

export const propostaMetaService = {
  /**
   * Extrai os metadados da proposta e os itens reais de serviço da lista de detalhes_do_pedido
   */
  separateItemsAndMeta(detalhesDoPedidoList: any[] = []): { items: any[]; meta: PropostaMeta } {
    const items: any[] = [];
    let meta: PropostaMeta = {};

    for (const item of detalhesDoPedidoList) {
      if (
        (typeof item.id === 'string' && item.id.startsWith('meta_pedido_')) ||
        (typeof item.servicos === 'string' && item.servicos.startsWith(META_PREFIX))
      ) {
        try {
          const jsonStr = item.servicos.replace(META_PREFIX, '');
          meta = JSON.parse(jsonStr);
        } catch (e) {
          console.warn('Erro ao decodificar metadados da proposta:', e);
        }
      } else {
        items.push(item);
      }
    }

    return { items, meta };
  },

  /**
   * Salva os metadados da proposta no banco Supabase (tabela detalhes_do_pedido) e no localStorage
   */
  async saveMeta(pedidoId: number, meta: PropostaMeta, defaultTipoServicoId: number = 1): Promise<void> {
    const metaStr = META_PREFIX + JSON.stringify(meta);
    const metaId = `meta_pedido_${pedidoId}`;

    // 1. Salvar no Supabase na tabela detalhes_do_pedido
    try {
      const { error } = await supabase.from('detalhes_do_pedido').upsert(
        {
          id: metaId,
          pedido_id: pedidoId,
          tipo_servico_id: defaultTipoServicoId,
          servicos: metaStr,
          quantidade: 0,
          valor_unitario: 0,
          valor_total: 0,
        },
        { onConflict: 'id' }
      );

      if (error) {
        console.warn('Aviso ao salvar meta da proposta no Supabase:', error.message);
      }
    } catch (err) {
      console.warn('Exceção ao salvar meta da proposta no Supabase:', err);
    }

    // 2. Salvar no localStorage como backup local instantâneo
    try {
      localStorage.setItem(`proposta_meta_${pedidoId}`, JSON.stringify(meta));
    } catch (e) {
      // Ignore quota storage errors
    }
  },

  /**
   * Obtém os metadados da proposta por pedidoId
   */
  async getMeta(pedidoId: number, detalhesDoPedidoList?: any[]): Promise<PropostaMeta> {
    // 1. Se os detalhes foram fornecidos, tenta extrair de lá
    if (detalhesDoPedidoList && detalhesDoPedidoList.length > 0) {
      const { meta } = this.separateItemsAndMeta(detalhesDoPedidoList);
      if (Object.keys(meta).length > 0) {
        return meta;
      }
    }

    // 2. Busca diretamente no Supabase em detalhes_do_pedido
    try {
      const { data } = await supabase
        .from('detalhes_do_pedido')
        .select('*')
        .eq('id', `meta_pedido_${pedidoId}`)
        .maybeSingle();

      if (data && data.servicos && data.servicos.startsWith(META_PREFIX)) {
        const jsonStr = data.servicos.replace(META_PREFIX, '');
        return JSON.parse(jsonStr);
      }
    } catch (e) {
      // Fallback para local storage
    }

    // 3. Fallback para localStorage
    try {
      const localStr = localStorage.getItem(`proposta_meta_${pedidoId}`);
      if (localStr) {
        return JSON.parse(localStr);
      }
    } catch (e) {
      // Ignore
    }

    return {};
  },

  /**
   * Enriquece uma lista de pedidos (propostas) com seus metadados
   */
  enrichProposta(pedido: any): any {
    if (!pedido) return pedido;
    const rawDetails = pedido.detalhes_do_pedido || [];
    const { items, meta } = this.separateItemsAndMeta(rawDetails);

    // Se não encontrou meta nos detalhes, tenta recuperar do localStorage
    let finalMeta = meta;
    if (Object.keys(finalMeta).length === 0 && pedido.id) {
      try {
        const localStr = localStorage.getItem(`proposta_meta_${pedido.id}`);
        if (localStr) {
          finalMeta = JSON.parse(localStr);
        }
      } catch (e) {
        // Ignore
      }
    }

    return {
      ...pedido,
      detalhes_do_pedido: items,
      status: finalMeta.status || pedido.status || 'Rascunho',
      consideracoes: finalMeta.consideracoes || pedido.consideracoes || '',
      condicoes_comerciais: finalMeta.condicoes_comerciais || pedido.condicoes_comerciais || '',
      condicoes_pagamento: finalMeta.condicoes_pagamento || pedido.condicoes_pagamento || '',
      total_geral: finalMeta.total_geral || pedido.total_geral || 0,
    };
  },
};
